/**
 * Offers & pricing facts (§C) — ported from the wizard's StepOffers so the
 * page stays the ONLY editing surface (design spec hard constraint: no
 * capability drop once /brand-bible/wizard redirects). Same fields, same
 * store, same Core-token primitives; wrapped in a collapsible card so the
 * already-busy Service section doesn't grow unbounded by default.
 * Guarantee is stored verbatim — agents quote, never paraphrase (contract §2.2).
 *
 * Direction Cc/Cd/Ce (gap pass):
 *  - guarantees are a LIST (multiple `kind:'guarantee'` rows), not a single row.
 *  - promotions carry amount/terms/editable channels/approved_for_public, gated
 *    by the DB CHECK bb_offers_verified_gate (NOT approved_for_public OR
 *    verified_by IS NOT NULL) — the toggle is disabled until verified.
 *  - the panel defaults open while §C is incomplete (it's a launch gate, it
 *    must not hide its own required fields) and collapses to a one-line
 *    ledger once complete.
 *  - dispatch fee / financing split their old single-line inputs into typed
 *    fields (amount+terms / provider+claim) with a no-data-loss read-back for
 *    legacy rows that only have the old combined string.
 *
 * EXPLICIT SAVE (bb-phase2-explicit-save-brief-20260810): this panel owns
 * letter **C**'s registration. Content edits buffer in `useOffersSaveBuffer`
 * and commit through the SAME `upsertEntry`/`discardEntry` store actions when
 * the page's "Save C" button fires; `discard()` drops the buffer and the
 * fields fall back to what is stored. Every input is therefore CONTROLLED —
 * a `defaultValue` input cannot be reset by a discard, and (the §C bug PR
 * #1607 fixed) a blur-committed uncontrolled input silently loses whatever
 * was typed when something disables it mid-type.
 *
 * Still immediate, by contract: Verify and "Approved for public use". Both
 * are operator acts on a persisted row rather than content edits, and the
 * verified-gate CHECK means approval is only ever meaningful for a row that
 * already exists server-side.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, OfferKind } from '@/features/brand-bible-wizard/types';
import {
  EnvelopeBadge,
  ExplicitNoneButton,
  Field,
  StepCard,
  VerificationRow,
  inputClass,
} from '@/components/brand-bible-wizard/primitives';
import { useRegisterBbSectionSave, type BbSectionSaveRegistration } from './save';
import { useOffersSaveBuffer, type OfferRow } from './offersSaveBuffer';

type PromoStatus = 'live' | 'scheduled' | 'expired';

function promoStatus(promo: Pick<BbEntry<'offers'>, 'effective_from' | 'effective_to'>): PromoStatus {
  const today = new Date().toISOString().slice(0, 10);
  if (promo.effective_to && promo.effective_to < today) return 'expired';
  if (promo.effective_from && promo.effective_from > today) return 'scheduled';
  return 'live';
}

const STATUS_BADGE_KIND: Record<PromoStatus, 'verified' | 'low' | 'rejected'> = {
  live: 'verified',
  scheduled: 'low',
  expired: 'rejected',
};

export function OffersPanel({ canVerify }: { canVerify: boolean }) {
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  const offers = useBrandBibleWizardStore(selectEntries('offers')).filter((o) => o.deleted_at === null);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';

  const { rows, changeCount, setField, setFields, addRow, addOrMergeSingleton, removeRow, save, discard } =
    useOffersSaveBuffer(offers, upsertEntry, discardEntry, bbTenant);

  // The registration object's identity has to change whenever dirtiness or the
  // change count does: the registry only notifies its subscribers on
  // register/save/discard, so a registration memoized on [] would leave the
  // footer's "Save C · n changes" label frozen at whatever it first read.
  const registration = useMemo<BbSectionSaveRegistration>(
    () => ({
      isDirty: () => changeCount > 0,
      changeCount: () => changeCount,
      save,
      discard,
    }),
    [changeCount, save, discard],
  );
  useRegisterBbSectionSave('C', registration);

  const byKind = (k: OfferKind) => rows.filter((o) => o.kind === k);
  const guarantees = byKind('guarantee');
  const dispatch = byKind('dispatch_fee')[0] ?? null;
  const financing = byKind('financing')[0] ?? null;
  const promotions = byKind('promotion');

  /** Immediate, non-buffered writes: verification and public approval only. */
  const approveKind = (row: OfferRow, approved: boolean) =>
    void upsertEntry('offers', { kind: row.kind, approved_for_public: approved }, row.id, bbTenant);

  // Per-entry opt-out of the split-field UX for legacy dispatch/financing rows
  // whose data still lives as one combined string — session-local, never persisted.
  const [keepCombined, setKeepCombined] = useState<Set<string>>(new Set());
  const [channelDraft, setChannelDraft] = useState<Record<string, string>>({});

  const filledCount = [guarantees.length > 0, !!dispatch, !!financing].filter(Boolean).length;
  const isComplete = filledCount === 3;
  const [open, setOpen] = useState(() => !isComplete);
  // isComplete is evaluated at mount before the store's entries hydrate (everything
  // still empty => filledCount 0 => "incomplete"), so the initial useState arg above
  // is just a reasonable first paint. Once entries actually load, sync open to the
  // real completeness — but only until the operator manually toggles, at which point
  // their choice wins for the rest of the session.
  const userToggledRef = useRef(false);
  useEffect(() => {
    if (!userToggledRef.current) setOpen(!isComplete);
  }, [isComplete]);

  // Combined provider+claim draft — see the input below for why this one field
  // buffers through a local string instead of directly.
  const combinedFinancingValue = financing
    ? `${financing.provider ?? ''} · ${financing.verbatim_text ?? ''}`
    : '';
  const [combinedFinancingDraft, setCombinedFinancingDraft] = useState(combinedFinancingValue);
  useEffect(() => {
    setCombinedFinancingDraft(combinedFinancingValue);
  }, [combinedFinancingValue]);

  // "Is this a legacy one-line row?" is a property of what is STORED, not of
  // what is currently typed — deciding it from the merged row would swap the
  // whole card's layout out from under the operator on their first keystroke.
  const storedById = (id: string) => offers.find((e) => e.id === id) ?? null;
  const storedDispatch = dispatch ? storedById(dispatch.id) : null;
  const storedFinancing = financing ? storedById(financing.id) : null;

  const dispatchLegacy =
    !!storedDispatch && !storedDispatch.amount && !storedDispatch.terms && !!storedDispatch.verbatim_text &&
    storedDispatch.verbatim_text !== 'none' && !keepCombined.has(storedDispatch.id);
  const dispatchKeepOne = !!dispatch && keepCombined.has(dispatch.id);
  const dispatchVerification = dispatch && (
    <VerificationRow
      verifiedBy={dispatch.verified_by}
      verifiedAt={dispatch.verified_at}
      canVerify={canVerify}
      onVerify={dispatch.isPending ? undefined : () => void verifyEntry('offers', dispatch.id, undefined, bbTenant)}
    />
  );

  const financingLegacy =
    !!storedFinancing && !storedFinancing.provider && !!storedFinancing.verbatim_text &&
    storedFinancing.verbatim_text !== 'none' && !keepCombined.has(storedFinancing.id);
  const financingKeepOne = !!financing && keepCombined.has(financing.id);
  const financingVerification = financing && (
    <VerificationRow
      verifiedBy={financing.verified_by}
      verifiedAt={financing.verified_at}
      canVerify={canVerify}
      onVerify={financing.isPending ? undefined : () => void verifyEntry('offers', financing.id, undefined, bbTenant)}
    />
  );

  return (
    <div className="col-span-2 rounded-lg border border-border bg-warm-bg">
      <button
        type="button"
        onClick={() => {
          userToggledRef.current = true;
          setOpen((v) => !v);
        }}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left"
      >
        {open ? <ChevronDown className="size-3.5 text-muted-foreground" /> : <ChevronRight className="size-3.5 text-muted-foreground" />}
        <span className="text-[12.5px] font-semibold">Offers &amp; pricing facts</span>
        {isComplete && !open ? (
          <span className="ml-auto font-mono text-[11px] text-teal">
            {filledCount} of 3 · {promotions.length} promo{promotions.length === 1 ? '' : 's'} ▸
          </span>
        ) : (
          <>
            <span className="text-[11px] text-muted-foreground">
              guarantee · dispatch fee · financing · promotions
            </span>
            <span className="ml-auto rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10.5px] text-teal">
              {filledCount}/3 + {promotions.length} promo{promotions.length === 1 ? '' : 's'}
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-border p-3.5">
          {changeCount > 0 && (
            <p data-testid="offers-unsaved-note" className="text-[11px] text-[hsl(var(--status-warning))]">
              {changeCount} unsaved change{changeCount === 1 ? '' : 's'} in this section — use Save C below.
            </p>
          )}
          {guarantees.map((g) => (
            <StepCard
              key={g.id}
              title="Guarantee"
              headerRight={
                <button
                  type="button"
                  onClick={() => removeRow(g)}
                  className="text-[11px] text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              }
            >
              <div className="flex flex-col gap-2.5 p-4">
                <Field
                  label="Exact wording"
                  required
                  hint="stored verbatim — agents may quote, never paraphrase"
                  verification={
                    <VerificationRow
                      verifiedBy={g.verified_by}
                      verifiedAt={g.verified_at}
                      canVerify={canVerify}
                      onVerify={g.isPending ? undefined : () => void verifyEntry('offers', g.id, undefined, bbTenant)}
                    />
                  }
                >
                  <input
                    className={cn(inputClass, 'text-[15px]')}
                    value={g.verbatim_text ?? ''}
                    placeholder={'e.g. "Priced before we start. Fixed right or we come back free."'}
                    onChange={(e) => setField(g, 'verbatim_text', e.target.value)}
                  />
                </Field>
                <label
                  className={cn(
                    'flex items-center gap-1.5 text-[11px]',
                    g.verified_by ? 'text-teal-dark' : 'cursor-not-allowed text-muted-foreground opacity-60',
                  )}
                  title={g.verified_by ? undefined : 'Verify first to allow public use'}
                >
                  <input
                    type="checkbox"
                    checked={!!g.approved_for_public}
                    disabled={!g.verified_by}
                    onChange={(e) => approveKind(g, e.target.checked)}
                    className="accent-coral disabled:cursor-not-allowed"
                  />
                  Approved for public use
                </label>
                {!g.verified_by && (
                  <p className="text-[11px] text-muted-foreground">Verify first to allow public use.</p>
                )}
              </div>
            </StepCard>
          ))}
          <button
            type="button"
            onClick={() => addRow('guarantee', { verbatim_text: '' })}
            className="self-start rounded-lg border border-dashed border-input px-3.5 py-2 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark"
          >
            + Add guarantee
          </button>

          <StepCard title="Dispatch fee">
            {dispatchLegacy ? (
              <div className="flex flex-col gap-2.5 p-4">
                <Field
                  label="Fee & terms (legacy — stored as one line)"
                  hint="split into amount + terms, or keep editing it as one line"
                  verification={dispatchVerification}
                >
                  <input className={cn(inputClass, 'tabular-nums')} value={dispatch!.verbatim_text ?? ''} readOnly />
                </Field>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const [amount, ...rest] = (dispatch!.verbatim_text ?? '').split('·').map((s) => s.trim());
                      const terms = rest.join(' · ');
                      // Keep verbatim_text in sync with the split fields — any agent
                      // consumer still reading the combined string must never see a
                      // stale value once amount/terms have moved on.
                      setFields(dispatch!, {
                        amount,
                        terms,
                        verbatim_text: `${amount}${terms ? ' · ' + terms : ''}`,
                      });
                    }}
                    className="rounded-lg border border-dashed border-input px-3 py-1.5 text-xs font-medium text-coral hover:border-coral"
                  >
                    Accept split
                  </button>
                  <button
                    type="button"
                    onClick={() => setKeepCombined((s) => new Set(s).add(dispatch!.id))}
                    className="rounded-lg border border-dashed border-input px-3 py-1.5 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark"
                  >
                    Keep as one line
                  </button>
                </div>
              </div>
            ) : dispatchKeepOne ? (
              <div className="flex items-end gap-3.5 p-4">
                <div className="flex-1">
                  <Field label="Fee & terms" verification={dispatchVerification}>
                    <input
                      className={cn(inputClass, 'tabular-nums')}
                      value={dispatch!.verbatim_text ?? ''}
                      placeholder="e.g. $79 · waived with completed repair"
                      onChange={(e) => setField(dispatch!, 'verbatim_text', e.target.value)}
                    />
                  </Field>
                </div>
                <ExplicitNoneButton
                  label={'No dispatch fee — record "none"'}
                  disabled={!!dispatch}
                  onClick={() => addOrMergeSingleton('dispatch_fee', { verbatim_text: 'none' })}
                />
              </div>
            ) : (
              <div className="flex items-end gap-3.5 p-4">
                <div className="flex-1">
                  <Field label="Amount" verification={dispatchVerification}>
                    <input
                      className={cn(inputClass, 'tabular-nums')}
                      value={dispatch?.amount ?? ''}
                      placeholder="e.g. $79"
                      onChange={(e) =>
                        dispatch
                          ? setField(dispatch, 'amount', e.target.value)
                          : addOrMergeSingleton('dispatch_fee', { amount: e.target.value })
                      }
                    />
                  </Field>
                </div>
                <div className="flex-1">
                  <Field label="Terms">
                    <input
                      className={inputClass}
                      value={dispatch?.terms ?? ''}
                      placeholder="e.g. waived with completed repair"
                      onChange={(e) =>
                        dispatch
                          ? setField(dispatch, 'terms', e.target.value)
                          : addOrMergeSingleton('dispatch_fee', { terms: e.target.value })
                      }
                    />
                  </Field>
                </div>
                <ExplicitNoneButton
                  label={'No dispatch fee — record "none"'}
                  disabled={!!dispatch}
                  onClick={() => addOrMergeSingleton('dispatch_fee', { verbatim_text: 'none' })}
                />
              </div>
            )}
          </StepCard>

          <StepCard title="Financing">
            {financingLegacy ? (
              <div className="flex flex-col gap-2.5 p-4">
                <Field
                  label="Provider & approved claim (legacy — stored as one line)"
                  hint="split into provider + claim, or keep editing it as one line"
                  verification={financingVerification}
                >
                  <input className={inputClass} value={financing!.verbatim_text ?? ''} readOnly />
                </Field>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const [provider, ...rest] = (financing!.verbatim_text ?? '').split('·').map((s) => s.trim());
                      setFields(financing!, { provider, verbatim_text: rest.join(' · ') });
                    }}
                    className="rounded-lg border border-dashed border-input px-3 py-1.5 text-xs font-medium text-coral hover:border-coral"
                  >
                    Accept split
                  </button>
                  <button
                    type="button"
                    onClick={() => setKeepCombined((s) => new Set(s).add(financing!.id))}
                    className="rounded-lg border border-dashed border-input px-3 py-1.5 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark"
                  >
                    Keep as one line
                  </button>
                </div>
              </div>
            ) : financingKeepOne ? (
              <div className="flex items-end gap-3.5 p-4">
                <div className="flex-1">
                  <Field label="Provider & approved claim" hint="the only wording agents may use" verification={financingVerification}>
                    {/* The one field whose control value is NOT the buffered value:
                        it packs two columns into a single string, so splitting on
                        every keystroke would wipe the claim the moment the operator
                        starts retyping the provider. Split on blur, exactly as this
                        field always did; the local draft reseeds from the merged
                        row, so a Discard restores it like every other input. */}
                    <input
                      className={inputClass}
                      value={combinedFinancingDraft}
                      placeholder={'e.g. Synchrony · approved claim: "financing available" only'}
                      onChange={(e) => setCombinedFinancingDraft(e.target.value)}
                      onBlur={(e) => {
                        const [provider, ...rest] = e.target.value.split('·').map((s) => s.trim());
                        setFields(financing!, { provider, verbatim_text: rest.join(' · ') });
                      }}
                    />
                  </Field>
                </div>
                <ExplicitNoneButton
                  label={'No financing — record "none"'}
                  disabled={!!financing}
                  onClick={() => addOrMergeSingleton('financing', { verbatim_text: 'none' })}
                />
              </div>
            ) : (
              <div className="flex items-end gap-3.5 p-4">
                <div className="flex-1">
                  <Field label="Provider" verification={financingVerification}>
                    <input
                      className={inputClass}
                      value={financing?.provider ?? ''}
                      placeholder="e.g. Synchrony"
                      onChange={(e) =>
                        financing
                          ? setField(financing, 'provider', e.target.value)
                          : addOrMergeSingleton('financing', { provider: e.target.value })
                      }
                    />
                  </Field>
                </div>
                <div className="flex-1">
                  <Field label="Approved claim" hint="the only wording agents may use">
                    <input
                      className={inputClass}
                      value={financing?.verbatim_text ?? ''}
                      placeholder={'e.g. "financing available"'}
                      onChange={(e) =>
                        financing
                          ? setField(financing, 'verbatim_text', e.target.value)
                          : addOrMergeSingleton('financing', { verbatim_text: e.target.value })
                      }
                    />
                  </Field>
                </div>
                <ExplicitNoneButton
                  label={'No financing — record "none"'}
                  disabled={!!financing}
                  onClick={() => addOrMergeSingleton('financing', { verbatim_text: 'none' })}
                />
              </div>
            )}
          </StepCard>

          {promotions.map((promo) => {
            const status = promoStatus(promo);
            const draft = channelDraft[promo.id] ?? '';
            const addChannel = () => {
              const val = draft.trim();
              if (!val) return;
              setField(promo, 'channels', [...(promo.channels ?? []), val]);
              setChannelDraft((d) => ({ ...d, [promo.id]: '' }));
            };
            return (
              <StepCard
                key={promo.id}
                title="Promotion"
                headerRight={
                  <>
                    <EnvelopeBadge kind={STATUS_BADGE_KIND[status]}>{status}</EnvelopeBadge>
                    {promo.verified_by && <EnvelopeBadge kind="verified">verified</EnvelopeBadge>}
                    {promo.approved_for_public && <EnvelopeBadge kind="public">approved for public</EnvelopeBadge>}
                    {/* Undo for an accidental "+ Add promotion" only. Deliberately
                        NOT offered for stored promotions: this panel has never had a
                        promotion-delete path, and a Save conversion is the wrong PR
                        to hand operators a new way to remove live client data. */}
                    {promo.isPending && (
                      <button
                        type="button"
                        onClick={() => removeRow(promo)}
                        className="text-[11px] text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </button>
                    )}
                  </>
                }
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 p-4">
                  <Field label="Offer">
                    <input
                      className={inputClass}
                      value={promo.title ?? ''}
                      onChange={(e) => setField(promo, 'title', e.target.value)}
                    />
                  </Field>
                  <Field label="Amount">
                    <input
                      className={cn(inputClass, 'tabular-nums')}
                      value={promo.amount ?? ''}
                      placeholder="e.g. $500 off"
                      onChange={(e) => setField(promo, 'amount', e.target.value)}
                    />
                  </Field>
                  <Field label="Runs from">
                    <input
                      type="date"
                      className={cn(inputClass, 'tabular-nums')}
                      value={promo.effective_from ?? ''}
                      onChange={(e) => setField(promo, 'effective_from', e.target.value || null)}
                    />
                  </Field>
                  <Field label="Ends">
                    <input
                      type="date"
                      className={cn(inputClass, 'tabular-nums')}
                      value={promo.effective_to ?? ''}
                      onChange={(e) => setField(promo, 'effective_to', e.target.value || null)}
                    />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Terms">
                      <textarea
                        className={cn(inputClass, 'min-h-[64px] resize-y')}
                        value={promo.terms ?? ''}
                        onChange={(e) => setField(promo, 'terms', e.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="Channels">
                      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-input bg-warm-bg px-3 py-2">
                        {(promo.channels ?? []).map((c) => (
                          <span
                            key={c}
                            className="inline-flex items-center gap-1 rounded-[5px] bg-teal-light py-0.5 pl-1.5 pr-1"
                          >
                            <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-teal">
                              {c}
                            </span>
                            <button
                              type="button"
                              aria-label={`Remove ${c}`}
                              onClick={() =>
                                setField(
                                  promo,
                                  'channels',
                                  (promo.channels ?? []).filter((x) => x !== c),
                                )
                              }
                              className="text-teal hover:text-destructive"
                            >
                              <X className="size-2.5" />
                            </button>
                          </span>
                        ))}
                        <input
                          value={draft}
                          onChange={(e) => setChannelDraft((d) => ({ ...d, [promo.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addChannel();
                            }
                          }}
                          onBlur={addChannel}
                          placeholder="+ channel"
                          className="min-w-[90px] flex-1 bg-transparent text-[11px] outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                    </Field>
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center justify-between gap-3.5">
                    <VerificationRow
                      verifiedBy={promo.verified_by}
                      verifiedAt={promo.verified_at}
                      canVerify={canVerify}
                      onVerify={promo.isPending ? undefined : () => void verifyEntry('offers', promo.id, undefined, bbTenant)}
                    />
                    <label
                      className={cn(
                        'flex items-center gap-1.5 text-[11px]',
                        promo.verified_by ? 'text-teal-dark' : 'cursor-not-allowed text-muted-foreground opacity-60',
                      )}
                      title={promo.verified_by ? undefined : 'Verify first to allow public use'}
                    >
                      <input
                        type="checkbox"
                        checked={!!promo.approved_for_public}
                        disabled={!promo.verified_by}
                        onChange={(e) => approveKind(promo, e.target.checked)}
                        className="accent-coral disabled:cursor-not-allowed"
                      />
                      Approved for public use
                    </label>
                  </div>
                  {!promo.verified_by && (
                    <p className="col-span-2 text-[11px] text-muted-foreground">
                      Verify first to allow public use.
                    </p>
                  )}
                </div>
                {promo.effective_to && (
                  <p className="px-4 pb-4 text-xs text-[hsl(var(--status-warning))]">
                    Self-expires after {promo.effective_to} — past the end date this promotion is invisible to every
                    agent automatically.
                  </p>
                )}
              </StepCard>
            );
          })}
          <button
            type="button"
            onClick={() => addRow('promotion', { title: '' })}
            className="self-start rounded-lg border border-dashed border-input px-3.5 py-2 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark"
          >
            + Add promotion
          </button>
        </div>
      )}
    </div>
  );
}
